import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { AutoTextarea } from '../../shared/ui/AutoTextarea'
import {
  SUPPLIER_PAYMENT_LABELS,
  SUPPLIER_TYPE_LABELS,
  type Supplier,
  type SupplierBody,
  type SupplierBuyer,
  type SupplierPaymentType,
  type SupplierType,
} from '../../shared/api/suppliers.api'
import { SupplierDuplicateHint } from './components/SupplierDuplicateHint'

// Модалка карточки поставщика (создание/правка). Поля — схема из файла РОЗ
// «Условия/Объём CNY»: тип, ответственный, условия оплаты, лимит, гарантия,
// рекламации, заметки.

const inputCls =
  'w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand-500'

export function SupplierEditModal({
  supplier,
  buyers,
  onSave,
  onClose,
  canEditBuyer = true,
}: {
  supplier: Supplier | null
  buyers: SupplierBuyer[]
  onSave: (body: SupplierBody) => Promise<void>
  onClose: () => void
  /**
   * Можно ли менять ответственного закупщика. РОП правит имя и условия
   * поставщиков РФ, но не ответственного — поле скрыто и не отправляется.
   */
  canEditBuyer?: boolean
}) {
  const [name, setName] = useState(supplier?.name ?? '')
  const [type, setType] = useState<SupplierType | ''>(supplier?.type ?? '')
  const [buyerId, setBuyerId] = useState(supplier?.responsibleBuyerId ?? '')
  const [paymentType, setPaymentType] = useState<SupplierPaymentType | ''>(
    supplier?.paymentType ?? '',
  )
  const [creditLimit, setCreditLimit] = useState(supplier?.creditLimitAmount ?? '')
  const [creditCurrency, setCreditCurrency] = useState(supplier?.creditLimitCurrency ?? 'CNY')
  const [postShipmentDays, setPostShipmentDays] = useState(
    supplier?.postShipmentDays?.toString() ?? '',
  )
  const [warrantyDays, setWarrantyDays] = useState(supplier?.warrantyDays?.toString() ?? '')
  const [complaintsCount, setComplaintsCount] = useState(
    supplier?.complaintsCount?.toString() ?? '0',
  )
  const [complaintsNotes, setComplaintsNotes] = useState(supplier?.complaintsNotes ?? '')
  const [paymentNotes, setPaymentNotes] = useState(supplier?.paymentNotes ?? '')
  const [err, setErr] = useState<unknown>(null)
  const [saving, setSaving] = useState(false)
  const navigate = useNavigate()
  // Подсказку дублей показываем при создании и переименовании: у карточки,
  // которую просто дозаполняют, имя не меняется — спрашивать не о чем.
  const nameChanged = !supplier || name.trim().toLowerCase() !== supplier.name.trim().toLowerCase()

  const numOrNull = (s: string): number | null => {
    const n = parseFloat(s.replace(',', '.'))
    return isNaN(n) ? null : n
  }
  const intOrNull = (s: string): number | null => {
    const n = parseInt(s, 10)
    return isNaN(n) ? null : n
  }

  const save = async () => {
    if (!name.trim() || saving) return
    setSaving(true)
    setErr(null)
    try {
      await onSave({
        name: name.trim(),
        type: type || null,
        ...(canEditBuyer ? { responsibleBuyerId: buyerId || null } : {}),
        paymentType: paymentType || null,
        creditLimitAmount: numOrNull(creditLimit),
        creditLimitCurrency: creditCurrency.trim() || null,
        postShipmentDays: intOrNull(postShipmentDays),
        warrantyDays: intOrNull(warrantyDays),
        complaintsCount: intOrNull(complaintsCount) ?? 0,
        complaintsNotes: complaintsNotes.trim() || null,
        paymentNotes: paymentNotes.trim() || null,
      })
      onClose()
    } catch (e) {
      setErr(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={() => !saving && onClose()}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">
            {supplier ? `Поставщик «${supplier.name}»` : 'Новый поставщик'}
          </h3>
          <button
            onClick={() => !saving && onClose()}
            className="text-gray-400 hover:text-gray-700 text-lg leading-none"
          >
            ×
          </button>
        </div>

        {err != null && <ErrorMessage error={err} />}

        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Название *</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          {nameChanged && (
            <SupplierDuplicateHint
              name={name}
              excludeId={supplier?.id}
              onOpen={(id) => {
                onClose()
                navigate(`/suppliers/${id}`)
              }}
            />
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Тип</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as SupplierType | '')}
              className={inputCls}
            >
              <option value="">—</option>
              {Object.entries(SUPPLIER_TYPE_LABELS).map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          {canEditBuyer && (
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Ответственный закупщик</label>
              <select
                value={buyerId}
                onChange={(e) => setBuyerId(e.target.value)}
                className={inputCls}
              >
                <option value="">Не назначен (видит тот, кто занёс)</option>
                {buyers.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name || b.email}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Условия оплаты</label>
            <select
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value as SupplierPaymentType | '')}
              className={inputCls}
            >
              <option value="">—</option>
              {Object.entries(SUPPLIER_PAYMENT_LABELS).map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Кредитный лимит</label>
            <div className="flex gap-1">
              <input
                type="number"
                value={creditLimit}
                onChange={(e) => setCreditLimit(e.target.value)}
                placeholder="100000"
                className={`${inputCls} flex-1`}
              />
              <select
                value={creditCurrency}
                onChange={(e) => setCreditCurrency(e.target.value)}
                className="w-18 px-1 py-1.5 border border-gray-300 rounded text-sm bg-white"
              >
                <option>CNY</option>
                <option>USD</option>
                <option>EUR</option>
                <option>RUB</option>
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">
              Отсрочка после отгрузки, дней
            </label>
            <input
              type="number"
              value={postShipmentDays}
              onChange={(e) => setPostShipmentDays(e.target.value)}
              placeholder="30"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Гарантия, дней</label>
            <input
              type="number"
              value={warrantyDays}
              onChange={(e) => setWarrantyDays(e.target.value)}
              placeholder="365"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Рекламаций</label>
            <input
              type="number"
              value={complaintsCount}
              onChange={(e) => setComplaintsCount(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Заметка по рекламациям</label>
          <AutoTextarea
            value={complaintsNotes}
            onChange={(e) => setComplaintsNotes(e.target.value.slice(0, 2000))}
            minRows={1}
            className={inputCls}
            placeholder="3 шт, ждём замену…"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Спецусловия оплаты</label>
          <AutoTextarea
            value={paymentNotes}
            onChange={(e) => setPaymentNotes(e.target.value.slice(0, 2000))}
            minRows={1}
            className={inputCls}
            placeholder="разъёмы 50/50, ступенчатые лимиты…"
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={() => !saving && onClose()}
            className="px-3 py-1.5 text-sm text-gray-600 rounded-lg border border-gray-200 hover:bg-gray-50"
          >
            Отмена
          </button>
          <button
            onClick={() => void save()}
            disabled={!name.trim() || saving}
            className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
          >
            {saving ? '…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  )
}
